import React, { useState } from "react";
import { Form, Input, Row, Col, Typography, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonComponent } from "@comps/components";
import { reset } from "../requests";
import { ArrowLeftOutlined, LockOutlined } from "@ant-design/icons";
import "./SignIn.css";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { authTooltips } from "../../common/authTooltips";

const fieldLabel = makeFieldLabel(authTooltips.auth, { namespace: "auth" });

const { Title, Text } = Typography;

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const params = new URLSearchParams(location.search);
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const hasResetLink = Boolean(email && token);

  const onFinish = async (values) => {
    if (!hasResetLink) {
      message.warning("This reset link is invalid or expired.");
      return;
    }

    setLoading(true);
    const res = await reset({
      email,
      token,
      password: values.password,
      password_confirmation: values.password_confirmation,
    });
    setLoading(false);

    if (res?.success || res?.code === 200) {
      message.success("Your password has been updated. Please sign in.");
      navigate("/", { replace: true });
      return;
    }

    message.warning(res?.response?.data?.message || res?.message || "This reset link is invalid or expired.");
  };

  return (
    <div className="signInPage">
      <Row className="signInLayout">
        <Col xs={ { span: 0 } } lg={ { span: 13 } } className="signInShowcaseCol">
          <div className="signInShowcase">
            <div className="signInSheen" aria-hidden="true" />
            <div className="signInBrand">
              <img src="/loginicon/image170.svg" alt="KLOK logo" />
              <div className="signInBrandText">
                <h1>KLOK</h1>
                <p>Set a new password and return securely to your HR workspace.</p>
              </div>
            </div>

            <div className="signInFeaturePanel">
              <span className="signInBadge">Secure Account Recovery</span>
              <h2>
                Create a fresh<br />
                password for your<br />
                <span>HR workspace.</span>
              </h2>
              <p>
                This reset link is time-limited and can only be used once.
              </p>
            </div>
          </div>
        </Col>

        <Col xs={ { span: 24 } } lg={ { span: 11 } } className="signInFormCol">
          <div className="signInFormShell">
            <div className="signInCard">
              <div className="signInCardHeader">
                <img src="/loginicon/image170.svg" alt="KLOK logo" />
                <img src="/loginicon/KLOK.svg" alt="KLOK" />
              </div>

              <Title className="signInHeading">Reset password</Title>
              <Text className="signInSubtitle">
                Choose a new password with at least 8 characters.
              </Text>

              <Form
                layout="vertical"
                name="reset-password"
                className="signInForm"
                onFinish={ onFinish }
              >
                <Form.Item
                  name="password"
                  label={ fieldLabel("password", "New password") }
                  rules={ [
                    { required: true, message: "Please input your new password!" },
                    { min: 8, message: "Minimum password length is 8" },
                    { max: 30, message: "Maximum password length is 30" },
                  ] }
                >
                  <Input.Password
                    placeholder="Enter new password"
                    prefix={ <LockOutlined /> }
                    disabled={ !hasResetLink }
                  />
                </Form.Item>

                <Form.Item
                  name="password_confirmation"
                  label={ fieldLabel("password_confirmation", "Confirm password") }
                  dependencies={ ["password"] }
                  rules={ [
                    { required: true, message: "Please confirm your new password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match."));
                      },
                    }),
                  ] }
                >
                  <Input.Password
                    placeholder="Confirm new password"
                    prefix={ <LockOutlined /> }
                    disabled={ !hasResetLink }
                  />
                </Form.Item>

                {!hasResetLink && (
                  <Text className="signInResetError">
                    This reset link is invalid or expired.
                  </Text>
                )}

                <Form.Item className="da-mt-16 da-mb-8">
                  <ButtonComponent className="actionButton" type="primary" htmlType="submit" loading={ loading } disabled={ !hasResetLink }>
                    Update Password
                  </ButtonComponent>
                </Form.Item>

                <button
                  type="button"
                  className="signInBackLink"
                  onClick={ () => navigate("/", { replace: true }) }
                >
                  <ArrowLeftOutlined /> Back to sign in
                </button>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ResetPassword;
